import {
  removeMemberFromStudiosApi,
  shareMemberToStudiosApi,
} from '@api/companyApi';
import type {
  CompanyMemberDTO,
  CreateMemberDTO,
} from '@conversed/shared/src/central/dto/company';
import { toastErrorTranslated, toastSuccessTranslated } from '@helpers/toast';
import { mainActions } from '@store/reducers/mainHandler';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import useRole from '../roles/useRole';
import { useCompanySettingsActions } from './useCompanyActions';
import { useCompanySettings } from './useCompanySettings';

export const useMembersTable = () => {
  const { members } = useCompanySettings();
  const { removeMember, addMember } = useCompanySettingsActions();
  const { assignableRoles } = useRole();
  const dispatch = useDispatch();

  const toggleRoleForMember = useCallback(
    (member: CompanyMemberDTO, roleId: string) => {
      const { roles: memberRoles } = member;
      const newRoles = memberRoles.includes(roleId)
        ? memberRoles.filter((role) => role !== roleId)
        : [...memberRoles, roleId];

      addMember({
        email: member.email,
        roles: newRoles,
        firstName: member.firstName,
        lastName: member.lastName,
        inboxes: member.inboxes,
      });
    },
    [addMember],
  );

  const shareMemberToStudios = useCallback(
    async (member: CompanyMemberDTO, companyIds: string[]) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        const memberData: CreateMemberDTO = {
          email: member.email,
          firstName: member?.firstName || '',
          lastName: member?.lastName || '',
          roles: member?.roles || [],
          inboxes: member?.inboxes || [],
        };

        await shareMemberToStudiosApi(companyIds, memberData);

        dispatch(
          mainActions.updateMemberCompanyIds({
            memberId: member.id,
            companyIds,
          }),
        );

        toastSuccessTranslated('member-settings.share.success');
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
        throw error;
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  const removeMemberFromStudios = useCallback(
    async (memberId: string, companyIds: string[]) => {
      try {
        dispatch(mainActions.setUIState({ isLoading: true }));

        await removeMemberFromStudiosApi(memberId, companyIds);

        toastSuccessTranslated('member-settings.share.remove-success');
      } catch (error) {
        console.error(error);
        toastErrorTranslated(error);
        throw error;
      } finally {
        dispatch(mainActions.setUIState({ isLoading: false }));
      }
    },
    [dispatch],
  );

  return {
    members,
    assignableRoles,
    toggleRoleForMember,
    removeMember,
    shareMemberToStudios,
    removeMemberFromStudios,
  };
};
